import { useCallback, useEffect, useRef, useState } from "react";
import { MESSAGE_LIMIT_CONFIG, STATIC_GREETING } from "../../lib/constants";
import type { CheckpointResponse } from "../../lib/schemas";
import { studentService } from "../../lib/api/studentService";
import { broadcastEvent } from "../../lib/api/client";
import type { ChatMessage } from "../../lib/api/types";

export type { ChatMessage };

interface UseChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearError: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  // Checkpoint handling
  showCheckpoint: boolean;
  isCheckpointSubmitting: boolean;
  handleCheckpointSubmit: (response: CheckpointResponse) => Promise<void>;
  // Blocked state
  isBlocked: boolean;
  // Weekly Warning state
  weeklyWarningMessage: string | null;
  setWeeklyWarningMessage: (msg: string | null) => void;
}

export function useChat(): UseChatReturn {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "greeting",
      role: "assistant",
      content: STATIC_GREETING.message,
      suggestions: STATIC_GREETING.suggestions,
      showDishaContact: STATIC_GREETING.showDishaContact,
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Checkpoint state
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [isCheckpointSubmitting, setIsCheckpointSubmitting] = useState(false);

  // Blocked state
  const [isBlocked, setIsBlocked] = useState(false);

  // Weekly Warning state
  const [weeklyWarningMessage, setWeeklyWarningMessage] = useState<
    string | null
  >(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Need to scroll when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Tell the parent window when the AI starts/stops typing
  useEffect(() => {
    broadcastEvent(
      {
        source: "disha-ai",
        event: "LOADING_STATE_CHANGED",
        isLoading: isLoading,
      }
    );
  }, [isLoading]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading || isBlocked) return;

      // Hide checkpoint when user sends a message
      setShowCheckpoint(false);

      const userMessage: ChatMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: content.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsLoading(true);
      setError(null);

      try {
        const data = await studentService.sendMessage(content.trim(), conversationId);

        if (!conversationId) {
          setConversationId(data.conversationId);
        }

        // Handle Abuse Warnings (Rule-based blocks)
        if (data.response.isWarning && !data.response.dailyCapWarning) {
          const warningMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: "warning",
            content: data.response.message,
            suggestions: data.response.suggestions,
            timestamp: new Date(),
            isWarning: true,
            warningNumber: data.response.warningNumber,
            isBlocked: data.response.isBlocked,
          };

          setMessages((prev) => [...prev, warningMessage]);
          if (data.response.isBlocked) setIsBlocked(true);
        } else {
          // Handle Normal Messages
          const assistantMessage: ChatMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: data.response.message,
            suggestions: data.response.suggestions,
            showDishaContact: data.response.showDishaContact,
            timestamp: new Date(),
            showCheckpoint: data.response.checkpoint?.show,
          };

          setMessages((prev) => [...prev, assistantMessage]);

          // Handle Daily Cap Warnings (firstWarningAt & dailyMaxMessage) immediately after the assistant message
          if (data.response.dailyCapWarning) {
            const capWarningMsg: ChatMessage = {
              id: crypto.randomUUID(),
              role: "warning",
              content: data.response.dailyCapWarning.warningMessage || "",
              timestamp: new Date(),
              isWarning: true,
              // Map message count firstWarningAt to warningNumber (1) so the UI formats it correctly
              warningNumber:
                data.response.dailyCapWarning.messageCount ===
                  MESSAGE_LIMIT_CONFIG.firstWarningAt
                  ? 1
                  : 2,
              isBlocked:
                data.response.dailyCapWarning.messageCount >=
                MESSAGE_LIMIT_CONFIG.dailyMaxMessage,
            };

            // Slight delay ensures React processes the assistant message first, then the warning
            setTimeout(() => {
              setMessages((prev) => [...prev, capWarningMsg]);
              if (capWarningMsg.isBlocked) setIsBlocked(true);
            }, 100);
          }

          // Capture Weekly Warning Banner
          if (data.response.weeklyCapWarning?.isWarning) {
            setWeeklyWarningMessage(
              data.response.weeklyCapWarning.warningMessage,
            );
          }

          if (data.response.checkpoint?.show) setShowCheckpoint(true);
        }
      } catch (err: any) {
        if (err.code === "CONVERSATION_BLOCKED" || 
            err.code === "DAILY_LIMIT_REACHED" || 
            err.code === "WEEKLY_LIMIT_REACHED") {
          setIsBlocked(true);
          if (err.code === "DAILY_LIMIT_REACHED" || err.code === "WEEKLY_LIMIT_REACHED") {
            const capWarningMsg: ChatMessage = {
              id: crypto.randomUUID(),
              role: "warning",
              content: err.message,
              timestamp: new Date(),
              isWarning: true,
              isBlocked: true,
            };
            setMessages((prev) => [...prev, capWarningMsg]);
            return;
          }
        }
        const errorMessage =
          err instanceof Error ? err.message : "Something went wrong";
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
    [conversationId, isLoading, isBlocked],
  );

  const handleCheckpointSubmit = useCallback(
    async (checkpointResponse: CheckpointResponse) => {
      if (!conversationId || isCheckpointSubmitting) return;

      setIsCheckpointSubmitting(true);

      try {
        const data: any = await studentService.submitCheckpoint(conversationId, checkpointResponse);

        // Hide the checkpoint
        setShowCheckpoint(false);

        // Add the response message
        const responseMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: data.message,
          suggestions: data.suggestions,
          timestamp: new Date(),
        };

        setMessages((prev) => [...prev, responseMessage]);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to submit checkpoint";
        setError(errorMessage);
      } finally {
        setIsCheckpointSubmitting(false);
      }
    },
    [conversationId, isCheckpointSubmitting],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearError,
    messagesEndRef,
    showCheckpoint,
    isCheckpointSubmitting,
    handleCheckpointSubmit,
    isBlocked,
    weeklyWarningMessage,
    setWeeklyWarningMessage,
  };
}
